import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { getCourses, getMyEnrollments, enrollInCourse, formatApiError } from '../../lib/api';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { BookOpen, Users, Loader2, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function StudentCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('monthly');
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const [coursesData, enrollmentsData] = await Promise.all([
        getCourses(),
        getMyEnrollments()
      ]);
      setCourses(coursesData);
      setEnrollments(enrollmentsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  const handleEnroll = async () => {
    if (!selectedCourse) return;
    setError('');
    setEnrolling(true);

    try {
      await enrollInCourse(selectedCourse.id, subscriptionType);
      toast.success('¡Inscripción exitosa!');
      setSelectedCourse(null);
      fetchData();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="student-courses-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">
            Explorar Módulos
          </h1>
          <p className="text-[#737373] mt-2">Descubre nuestra oferta de formación misionera</p>
        </div>

        {/* Courses Grid */}
        {courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => {
              const enrolled = isEnrolled(course.id);
              return (
                <Card 
                  key={course.id}
                  data-testid={`course-card-${course.id}`}
                  className="bg-white border border-[#E5DFD3] shadow-sm course-card overflow-hidden"
                >
                  <div className="h-48 bg-gradient-to-br from-[#8B1538] to-[#70102B] flex items-center justify-center relative">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <BookOpen className="h-16 w-16 text-white/50" />
                    )}
                    {enrolled && (
                      <div className="absolute top-3 right-3 bg-[#2D6A4F] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Inscrito
                      </div>
                    )}
                  </div>
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#1A1A1A]">{course.title}</CardTitle>
                    <p className="text-sm text-[#737373]">Por {course.teacher_name}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#4A4A4A] line-clamp-3 mb-4">{course.description}</p>
                    <div className="flex items-center gap-4 text-sm text-[#737373]">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{course.enrolled_count} estudiantes</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        <span>{course.documents?.length || 0} documentos</span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="border-t border-[#E5DFD3] bg-[#FDFBF7] flex justify-between items-center">
                    <div>
                      <span className="text-xs text-[#737373]">Desde</span>
                      <p className="font-semibold text-[#8B1538]">${course.price_monthly.toLocaleString()}/mes</p>
                    </div>
                    {enrolled ? (
                      <Button 
                        data-testid={`view-course-btn-${course.id}`}
                        onClick={() => navigate(`/student/course/${course.id}`)}
                        className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
                      >
                        Ver Curso
                      </Button>
                    ) : (
                      <Button 
                        data-testid={`enroll-btn-${course.id}`}
                        onClick={() => {
                          setSelectedCourse(course);
                          setSubscriptionType('monthly');
                          setError('');
                        }}
                        className="bg-[#8B1538] hover:bg-[#70102B] text-white"
                      >
                        Inscribirse
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <BookOpen className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" />
            <h3 className="font-heading text-xl text-[#1A1A1A] mb-2">No hay módulos disponibles</h3>
            <p className="text-[#737373]">Próximamente nuevos programas</p>
          </div>
        )}

        {/* Enrollment Dialog */}
        <Dialog open={!!selectedCourse} onOpenChange={() => setSelectedCourse(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Inscribirse en {selectedCourse?.title}</DialogTitle>
              <DialogDescription>
                Selecciona tu plan de suscripción
              </DialogDescription>
            </DialogHeader>
            
            {error && (
              <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <RadioGroup value={subscriptionType} onValueChange={setSubscriptionType} className="space-y-4">
              <div className={`flex items-center justify-between p-4 border rounded-md cursor-pointer transition-colors ${subscriptionType === 'monthly' ? 'border-[#8B1538] bg-[#8B1538]/5' : 'border-[#E5DFD3] hover:bg-[#F5F2EA]'}`}>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="monthly" id="monthly" />
                  <Label htmlFor="monthly" className="cursor-pointer">
                    <span className="font-medium text-[#1A1A1A]">Mensual</span>
                    <p className="text-sm text-[#737373]">Pago cada mes</p>
                  </Label>
                </div>
                <span className="font-bold text-[#8B1538]">${selectedCourse?.price_monthly?.toLocaleString()}/mes</span>
              </div>
              
              <div className={`flex items-center justify-between p-4 border rounded-md cursor-pointer transition-colors ${subscriptionType === 'annual' ? 'border-[#8B1538] bg-[#8B1538]/5' : 'border-[#E5DFD3] hover:bg-[#F5F2EA]'}`}>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="annual" id="annual" />
                  <Label htmlFor="annual" className="cursor-pointer">
                    <span className="font-medium text-[#1A1A1A]">Anual</span>
                    <p className="text-sm text-[#737373]">Ahorra con el pago anual</p>
                  </Label>
                </div>
                <div className="text-right">
                  <span className="font-bold text-[#8B1538]">${selectedCourse?.price_annual?.toLocaleString()}/año</span>
                  <p className="text-xs text-[#2D6A4F]">Ahorra {Math.round(100 - (selectedCourse?.price_annual / (selectedCourse?.price_monthly * 12)) * 100)}%</p>
                </div>
              </div>
            </RadioGroup>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setSelectedCourse(null)} className="border-[#E5DFD3]">
                Cancelar
              </Button>
              <Button 
                data-testid="confirm-enroll-btn"
                onClick={handleEnroll}
                disabled={enrolling}
                className="bg-[#8B1538] hover:bg-[#70102B] text-white"
              >
                {enrolling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  'Confirmar Inscripción'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
